
import { RISK_CHART_MODES, VIEW } from 'interfaces';
import { includes } from 'lodash/fp';
import { inject } from 'mobx-react';
import rootStore, { IMobxRootState } from '../../RootStore';
import { E_RISK_TYPE } from '../../services/lib/APIServiceInterfaces';
import { COMPOSITE_DATA_INTERVAL } from './ChartStore';
import DataTypeToggle from './DataTypeToggle';
import PulseIcon from '../../assets/svg/Pulse.svg?react';
import PastProjectionsIcon from '../../assets/svg/PastProjections.svg?react';
import { FGIcons } from '@fg/design-system';
import moment from 'moment';
import { useEffect } from 'react';

type Props = {
    hasActiveCustomWeight?: boolean;
    riskChartMode?: RISK_CHART_MODES;
    hidePastProjectionButton?: boolean;
    hideToggleMap?: boolean;
    projectionDataOn?: boolean;
    riskMarkersOn?: boolean;
    isMonthly?: boolean;
    indicatorLabel?: React.ReactNode;
    hideRiskMarkerButton?: boolean;
    isFocusRisk?: boolean;
    virtualToday?: moment.Moment;
};

const LIMIT_REACHED_TOOLTIP =
    'To keep the chart readable, only 3 layers can be shown.';

export const TimelineButtons = inject(
    ({ risksStore, customWeightStore, chartStore }: IMobxRootState) => {
        return {
            riskChartMode: risksStore.riskChartMode,
            hasActiveCustomWeight: customWeightStore.hasActiveCustomWeight,
            currencyCountry: risksStore.getCurrencyCountry(),
            projectionDataOn:
                risksStore.projectionDataOn &&
                !customWeightStore.hasActiveCustomWeight,
            riskMarkersOn: risksStore.riskMarkersOn,
            isMonthly:
                chartStore.compositeDataInterval ===
                COMPOSITE_DATA_INTERVAL.MONTHLY,
            isFocusRisk:
                risksStore.currentRisksType === E_RISK_TYPE.CLIENT_FACING_INDICATORS,
            virtualToday: chartStore.virtualToday,
        };
    }
)(
    ({
        riskChartMode,
        hasActiveCustomWeight,
        hideToggleMap,
        projectionDataOn,
        hidePastProjectionButton,
        isMonthly,
        riskMarkersOn,
        indicatorLabel,
        hideRiskMarkerButton,
        isFocusRisk,
        virtualToday,
    }: Props) => {
        //todo: consider moving somewhere else
        useEffect(()=>{
            if (isFocusRisk){
                rootStore.risksStore.riskMarkersOn = false;
                rootStore.risksStore.projectionDataOn = false;
                rootStore.chartStore.riskMarkerEvent = null;
            }
        }, [isFocusRisk])
        const onRiskModeClick = () => {
            switch (riskChartMode) {
                case RISK_CHART_MODES.RISK:
                    break;
                case RISK_CHART_MODES.PULSE:
                    rootStore.risksStore.riskChartMode =
                        RISK_CHART_MODES.RISK_AND_PULSE;
                    break;
                case RISK_CHART_MODES.RISK_AND_PULSE: {
                    rootStore.risksStore.riskChartMode = RISK_CHART_MODES.PULSE;
                    if (projectionDataOn) {
                        toggleProjectionData();
                    }
                    break;
                }
            }
        };
        const onPulseModeClick = () => {
            switch (riskChartMode) {
                case RISK_CHART_MODES.PULSE:
                    break;
                case RISK_CHART_MODES.RISK:
                    rootStore.risksStore.riskChartMode =
                        RISK_CHART_MODES.RISK_AND_PULSE;
                    break;
                case RISK_CHART_MODES.RISK_AND_PULSE:
                    rootStore.risksStore.riskChartMode = RISK_CHART_MODES.RISK;
                    break;
            }
        };

        const onRiskMarkersClick = () => {
            const isRiskMarkersOn = rootStore.risksStore.riskMarkersOn;
            rootStore.risksStore.riskMarkersOn = !isRiskMarkersOn;
            if (isRiskMarkersOn && rootStore.chartStore.riskMarkerEvent) {
                rootStore.chartStore.riskMarkerEvent = null;
            }
        };

        //todo: remove this legacy function
        const currentPage = (path: string) => window.location.href.match(path);

        const toggleProjectionData = () => {
            rootStore.risksStore.projectionDataOn = !projectionDataOn;
            if (
                currentPage(VIEW.WORLD_GRAPH) ||
                currentPage(VIEW.COUNTRY_GRAPH)
            ) {
                switch (!projectionDataOn) {
                    case true:
                        rootStore.chartStore.setState({
                            showVirtualToday: true,
                        });
                        break;
                    case false:
                        rootStore.chartStore.setState({
                            showVirtualToday: true,
                        });
                        break;
                    default:
                        rootStore.chartStore.setState({
                            showVirtualToday: true,
                        });
                        break;
                }
            }
        };
        const pastProjectionsEnabled =
            !hasActiveCustomWeight &&
            riskChartMode !== RISK_CHART_MODES.PULSE &&
            !isMonthly;
        const isCompositeActive =
            hasActiveCustomWeight ||
            includes(riskChartMode, [
                RISK_CHART_MODES.RISK,
                RISK_CHART_MODES.RISK_AND_PULSE,
            ]);
        const isPulseActive = includes(riskChartMode, [
            RISK_CHART_MODES.PULSE,
            RISK_CHART_MODES.RISK_AND_PULSE,
        ]);
        const isLimitReached =
            [
                isCompositeActive,
                isPulseActive,
                projectionDataOn,
                riskMarkersOn,
            ].filter(Boolean).length >= 3;
        const isCompositeDisabled =
            hasActiveCustomWeight || (isLimitReached && !isCompositeActive);
        const isPulseDisabled =
            hasActiveCustomWeight ||
            isMonthly ||
            (isLimitReached && !isPulseActive);
        const isVirtualTodayInPast = virtualToday.isBefore(
            moment().startOf('day')
        );
        const isPastProjectionDisabled =
            isFocusRisk ||
            !pastProjectionsEnabled ||
            !isVirtualTodayInPast ||
            (isLimitReached && !projectionDataOn);
        const isRiskMarkersDisabled =
            isFocusRisk || isMonthly || (isLimitReached && !riskMarkersOn);
        return (
            <div className="timeline-buttons">
                {!hideToggleMap && (
                    <>
                        <DataTypeToggle
                            title="Composite"
                            active={isCompositeActive}
                            onClick={onRiskModeClick}
                            tooltip={
                                isLimitReached && isRiskMarkersDisabled
                                    ? LIMIT_REACHED_TOOLTIP
                                    : 'Composite'
                            }
                            iconComponent={<FGIcons icon="Delete" />}
                            disabled={isCompositeDisabled}
                        />
                        <DataTypeToggle
                            title="Pulse"
                            active={isPulseActive}
                            onClick={onPulseModeClick}
                            tooltip={
                                isLimitReached && isPulseDisabled
                                    ? LIMIT_REACHED_TOOLTIP
                                    : 'Pulse'
                            }
                            iconComponent={<PulseIcon />}
                            disabled={isPulseDisabled}
                        />
                    </>
                )}
                {!hidePastProjectionButton && (
                    <DataTypeToggle
                        title="Past projections"
                        active={projectionDataOn}
                        onClick={toggleProjectionData}
                        tooltip={
                            isLimitReached && isPastProjectionDisabled
                                ? LIMIT_REACHED_TOOLTIP
                                : riskChartMode === RISK_CHART_MODES.PULSE
                                ? 'Past projections can only be displayed for composite data'
                                : isFocusRisk
                                ? 'Past Projections aren’t available for Focus Risk types'
                                : !isVirtualTodayInPast
                                ? 'Move the indicator on the chart to a past date to view projections from that time'
                                : 'Past Projections'
                        }
                        iconComponent={<PastProjectionsIcon />}
                        disabled={isPastProjectionDisabled}
                    />
                )}
                {!hideRiskMarkerButton && (
                    <DataTypeToggle
                        title="Risk Hotspots"
                        active={riskMarkersOn}
                        onClick={onRiskMarkersClick}
                        tooltip={
                            isLimitReached && isRiskMarkersDisabled
                                ? LIMIT_REACHED_TOOLTIP
                                : isFocusRisk
                                ? 'Risk Hotspots aren’t available for Focus Risk types'
                                : 'Risk hotspots identify major high-frequency Risk scores – up (threat) or down (opportunity) – that influence a Composite Risk trend.'
                        }
                        iconComponent={<FGIcons icon="Circle" size={12} />}
                        disabled={isRiskMarkersDisabled}
                    />
                )}
                {indicatorLabel && <>{indicatorLabel}</>}
            </div>
        );
    }
);
